import { useState, useEffect } from 'react';
import { apiFetch } from '../../../lib/api';
import PasswordInput from '../../PasswordInput';

// ── AWS Regions ──────────────────────────────────────────────────────────────

const AWS_REGIONS = [
  { value: 'ap-south-1',     label: 'Asia Pacific — Mumbai' },
  { value: 'ap-southeast-1', label: 'Asia Pacific — Singapore' },
  { value: 'ap-southeast-2', label: 'Asia Pacific — Sydney' },
  { value: 'ap-northeast-1', label: 'Asia Pacific — Tokyo' },
  { value: 'ap-northeast-2', label: 'Asia Pacific — Seoul' },
  { value: 'ap-northeast-3', label: 'Asia Pacific — Osaka' },
  { value: 'ap-east-1',      label: 'Asia Pacific — Hong Kong' },
  { value: 'ap-south-2',     label: 'Asia Pacific — Hyderabad' },
  { value: 'ap-southeast-3', label: 'Asia Pacific — Jakarta' },
  { value: 'us-east-1',      label: 'US East — N. Virginia' },
  { value: 'us-east-2',      label: 'US East — Ohio' },
  { value: 'us-west-1',      label: 'US West — N. California' },
  { value: 'us-west-2',      label: 'US West — Oregon' },
  { value: 'eu-west-1',      label: 'Europe — Ireland' },
  { value: 'eu-west-2',      label: 'Europe — London' },
  { value: 'eu-west-3',      label: 'Europe — Paris' },
  { value: 'eu-central-1',   label: 'Europe — Frankfurt' },
  { value: 'eu-central-2',   label: 'Europe — Zurich' },
  { value: 'eu-north-1',     label: 'Europe — Stockholm' },
  { value: 'eu-south-1',     label: 'Europe — Milan' },
  { value: 'eu-south-2',     label: 'Europe — Spain' },
  { value: 'ca-central-1',   label: 'Canada — Central' },
  { value: 'sa-east-1',      label: 'South America — S\u00e3o Paulo' },
  { value: 'me-south-1',     label: 'Middle East — Bahrain' },
  { value: 'me-central-1',   label: 'Middle East — UAE' },
  { value: 'af-south-1',     label: 'Africa — Cape Town' },
  { value: 'il-central-1',   label: 'Israel — Tel Aviv' },
];

// ── Types ────────────────────────────────────────────────────────────────────

export interface BYOCCredentialsData {
  provider:     'AWS';
  region:       string;
  maskedKeyId:  string;
  customDomain: string | null;
}

interface Props {
  tenantId: string;
  onStepComplete: (data: BYOCCredentialsData) => void;
  savedData?: BYOCCredentialsData;
}

interface ValidationResult {
  valid: boolean;
  accountId?: string;
  error?: string;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: 'var(--font-body)', fontSize: 13,
  fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', background: 'var(--surface-alt)',
  color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 14,
  outline: 'none', boxSizing: 'border-box',
};

const helperStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12,
  color: 'var(--text-secondary)', marginTop: 4,
};

const fieldGap: React.CSSProperties = { marginBottom: 20 };

const btnPrimary: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
  padding: '10px 24px', background: 'var(--primary)', color: '#fff',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};

// ── Region Dropdown ──────────────────────────────────────────────────────────

function RegionDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? AWS_REGIONS.filter(r =>
        r.value.toLowerCase().includes(search.toLowerCase()) ||
        r.label.toLowerCase().includes(search.toLowerCase()))
    : AWS_REGIONS;

  const selected = AWS_REGIONS.find(r => r.value === value);
  const displayValue = selected ? `${selected.value} (${selected.label})` : value;

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        value={open ? search : displayValue}
        onChange={e => { setSearch(e.target.value); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        onBlur={() => setTimeout(() => { setOpen(false); setSearch(''); }, 200)}
        placeholder="Search AWS regions..."
        role="combobox"
        aria-expanded={open}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
          maxHeight: 240, overflowY: 'auto', marginTop: 2,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
        }}>
          {filtered.map(r => (
            <div
              key={r.value}
              onMouseDown={e => { e.preventDefault(); onChange(r.value); setOpen(false); setSearch(''); }}
              style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                fontFamily: 'var(--font-body)', color: 'var(--text)',
                background: r.value === value ? 'rgba(124,58,237,0.08)' : 'transparent',
                fontWeight: r.value === value ? 600 : 400,
              }}
            >
              {r.value} ({r.label})
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
              No regions match
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function BYOCCredentialsStep({ tenantId, onStepComplete, savedData }: Props) {
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState(savedData?.region ?? 'ap-south-1');
  const [customDomain, setCustomDomain] = useState(savedData?.customDomain ?? '');

  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [domainWarning, setDomainWarning] = useState('');

  // Rehydrate region from backend on mount (only if no savedData from wizard state).
  // GET /cloud returns the saved region from the encrypted blob; we use that to
  // repopulate the dropdown when a user returns to the BYOC step after a restart.
  // The `alive` flag prevents a late GET response from clobbering a region the
  // user has already changed via the dropdown while the GET was in flight.
  useEffect(() => {
    if (savedData?.region) return; // wizard state wins
    let alive = true;
    apiFetch<{ aws: { connected: boolean; region: string | null; maskedKeyId: string | null } }>(
      `/tenants/${tenantId}/credentials/cloud`,
    )
      .then(res => {
        if (alive && res.aws.connected && res.aws.region) {
          setRegion(res.aws.region);
        }
      })
      .catch(() => { /* no creds yet — fine */ });
    return () => { alive = false; };
  }, [tenantId, savedData?.region]);

  const canValidate = accessKeyId.length > 0 && secretAccessKey.length > 0;
  const isValidated = validation?.valid === true;

  const handleValidate = async () => {
    setValidating(true);
    setValidation(null);
    setSaveError('');
    try {
      const result = await apiFetch<ValidationResult>(
        `/tenants/${tenantId}/credentials/cloud/validate`,
        {
          method: 'POST',
          body: JSON.stringify({ provider: 'AWS', accessKeyId, secretAccessKey, region }),
        },
      );
      setValidation(result);
    } catch {
      setValidation({ valid: false, error: 'Request failed — check your network connection' });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setDomainWarning('');
    try {
      // Hard gate: credentials POST must succeed or the wizard does not progress.
      const result = await apiFetch<{ saved: boolean; maskedKeyId: string }>(
        `/tenants/${tenantId}/credentials/cloud`,
        {
          method: 'POST',
          body: JSON.stringify({ provider: 'AWS', accessKeyId, secretAccessKey, region }),
        },
      );
      if (!result.saved) return;

      // Best-effort: customDomain PATCH is optional and conditional on a non-empty value.
      // If the PATCH fails after a successful save, surface a non-blocking warning and
      // still call onStepComplete.
      const trimmedDomain = customDomain.trim();
      if (trimmedDomain) {
        try {
          await apiFetch(`/tenants/${tenantId}`, {
            method: 'PATCH',
            body: JSON.stringify({ customDomain: trimmedDomain }),
          });
        } catch {
          setDomainWarning('Credentials saved, but failed to set custom domain — set it later in Settings → Organisation.');
        }
      }

      onStepComplete({
        provider:     'AWS',
        region,
        maskedKeyId:  result.maskedKeyId,
        customDomain: trimmedDomain || null,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onStepComplete({ provider: 'AWS', region: '', maskedKeyId: '', customDomain: null });
  };

  const focusRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)';
    e.currentTarget.style.borderColor = 'var(--primary)';
  };
  const blurRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--border)';
  };

  return (
    <div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Connect your cloud account
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>
        Provide IAM credentials so BluePrint can provision infrastructure in your AWS account.
      </p>

      {/* Provider selector — AWS only for now */}
      <div style={fieldGap}>
        <label style={labelStyle}>Cloud Provider</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--primary)', background: 'rgba(124,58,237,0.06)',
            cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14,
            fontWeight: 600, color: 'var(--primary)',
          }}>
            <input type="radio" name="provider" value="AWS" checked readOnly style={{ accentColor: 'var(--primary)' }} />
            AWS
          </label>
          {['Azure', 'GCP'].map(p => (
            <label key={p} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 16px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', background: 'transparent',
              cursor: 'not-allowed', fontFamily: 'var(--font-body)', fontSize: 14,
              color: 'var(--text-secondary)', opacity: 0.5,
            }}>
              <input type="radio" name="provider" disabled style={{ accentColor: 'var(--primary)' }} />
              {p} <span style={{ fontSize: 11 }}>(coming soon)</span>
            </label>
          ))}
        </div>
      </div>

      {/* Access Key ID */}
      <div style={fieldGap}>
        <label style={labelStyle}>AWS Access Key ID</label>
        <input
          type="text"
          value={accessKeyId}
          onChange={e => { setAccessKeyId(e.target.value); setValidation(null); }}
          placeholder="AKIAIOSFODNN7EXAMPLE"
          style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
          onFocus={focusRing}
          onBlur={blurRing}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Secret Access Key */}
      <div style={fieldGap}>
        <label style={labelStyle}>AWS Secret Access Key</label>
        <PasswordInput
          value={secretAccessKey}
          onChange={e => { setSecretAccessKey(e.target.value); setValidation(null); }}
          placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
          style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
          onFocus={focusRing}
          onBlur={blurRing}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Region */}
      <div style={fieldGap}>
        <label style={labelStyle}>AWS Region</label>
        <RegionDropdown value={region} onChange={v => { setRegion(v); setValidation(null); }} />
      </div>

      {/* Custom Domain (optional) */}
      <div style={fieldGap}>
        <label style={labelStyle}>Custom Domain (optional)</label>
        <input
          type="text"
          value={customDomain}
          onChange={e => setCustomDomain(e.target.value)}
          placeholder="myapp.com"
          style={inputStyle}
          onFocus={focusRing}
          onBlur={blurRing}
          autoComplete="off"
          spellCheck={false}
        />
        <p style={helperStyle}>
          The domain BluePrint will deploy your application to. You can configure this later in Settings &rarr; Organisation.
        </p>
      </div>

      {/* IAM permissions info */}
      <div style={{
        marginBottom: 20, padding: '12px 14px',
        background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.15)',
        borderRadius: 'var(--radius-sm)',
      }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text)' }}>Required IAM permissions:</strong>{' '}
          AmazonEC2FullAccess &middot; AmazonRoute53FullAccess &middot; AmazonVPCFullAccess
        </p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
          <a
            href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--primary)', textDecoration: 'underline' }}
          >
            How to create IAM credentials &rarr;
          </a>
        </p>
      </div>

      {/* Validate button */}
      <div style={{ marginBottom: 20 }}>
        <button
          type="button"
          onClick={() => void handleValidate()}
          disabled={!canValidate || validating}
          style={{
            ...btnPrimary,
            background: 'transparent', color: 'var(--warning)',
            border: '1px solid rgba(245,158,11,0.5)',
            opacity: !canValidate || validating ? 0.5 : 1,
            cursor: !canValidate || validating ? 'not-allowed' : 'pointer',
          }}
        >
          {validating ? 'Validating...' : 'Validate Credentials'}
        </button>

        {validation && (
          <span style={{ marginLeft: 12, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500 }}>
            {validation.valid ? (
              <span style={{ color: 'var(--success)' }}>
                &#10003; Valid &mdash; Account: {validation.accountId}
              </span>
            ) : (
              <span style={{ color: 'var(--error)' }}>
                &#10007; {validation.error}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Warning banner — credentials saved but customDomain PATCH failed */}
      {domainWarning && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--warning)',
        }}>
          {domainWarning}
        </div>
      )}

      {/* Error banner */}
      {saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {saveError}
        </div>
      )}

      {/* Save and Continue */}
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={!isValidated || saving}
        style={{
          ...btnPrimary,
          opacity: !isValidated || saving ? 0.5 : 1,
          cursor: !isValidated || saving ? 'not-allowed' : 'pointer',
        }}
      >
        {saving ? 'Saving...' : 'Save and Continue \u2192'}
      </button>

      {/* Skip link */}
      <p style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={handleSkip}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
            cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          Skip for now &mdash; set up later in Settings &rarr; Organisation
        </button>
      </p>
    </div>
  );
}
